import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { CreditCard, ChevronDown, Loader2 } from 'lucide-react';
import AddonCounter from './AddonCounter';
import PriceEstimate from './PriceEstimate';

/**
 * Subscribe / Modify Add-ons card.
 * Renders one of two layouts based on `isActive`:
 *  • Static checkout form for trialing or expired users (calls onCheckout)
 *  • Collapsible modify-addons configurator for active subscribers (calls onModify)
 */
export default function SubscribeCard({
  sub, isActive, isProvider, planLabel, basePrice,
  extraOrgs, setExtraOrgs, extraCompanies, setExtraCompanies, extraAdmins, setExtraAdmins, extraUsers, setExtraUsers,
  estimate, usage,
  addonsOpen, setAddonsOpen,
  checkoutLoading, handleCheckout,
  hasChanges, openConfirm,
}) {
  const baseDesc = isProvider
    ? '3 orgs, 6 companies, 1 admin, 2 users, unlimited viewers & claims'
    : '1 company, 1 admin, 2 users, unlimited viewers & claims';

  return (
    <Card data-testid="subscribe-card">
      {isActive ? (
        <Collapsible open={addonsOpen} onOpenChange={setAddonsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-3 cursor-pointer hover:bg-gray-50/50 transition-colors rounded-t-lg" data-testid="modify-addons-toggle">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-teal-500" />
                  <div>
                    <CardTitle className="text-lg">Modify Add-ons</CardTitle>
                    <CardDescription className="mt-0.5">Adjust your add-ons — changes are prorated automatically</CardDescription>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${addonsOpen ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <BasePlanRow planLabel={planLabel} basePrice={basePrice} baseDesc={baseDesc} />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Add-ons</p>
                {isProvider && (
                  <AddonCounter label="Extra Organizations" value={extraOrgs} onChange={setExtraOrgs} price="30" />
                )}
                <AddonCounter label="Extra Companies" value={extraCompanies} onChange={setExtraCompanies} price={isProvider ? '9' : '29'} />
                <AddonCounter label="Extra Admins" value={extraAdmins} onChange={setExtraAdmins} price={isProvider ? '7' : '11'} />
                <AddonCounter label="Extra Users" value={extraUsers} onChange={setExtraUsers} price={isProvider ? '5' : '9'} />
              </div>
              <PriceEstimate
                estimate={estimate} isProvider={isProvider}
                extraOrgs={extraOrgs} extraCompanies={extraCompanies} extraAdmins={extraAdmins} extraUsers={extraUsers}
              />
              <Button
                onClick={openConfirm}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white h-11 text-base font-semibold"
                disabled={!hasChanges}
                data-testid="modify-plan-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" /> Update Plan — ${estimate?.total_monthly?.toFixed(2) || basePrice.replace('$', '')}/mo
              </Button>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-500" />
              <CardTitle className="text-lg">Subscribe to {planLabel} Plan</CardTitle>
            </div>
            <CardDescription>Configure your plan and checkout securely via Stripe</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <BasePlanRow planLabel={planLabel} basePrice={basePrice} baseDesc={baseDesc} />
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Add-ons</p>
                {sub.status === 'trialing' && (
                  <span className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-md" data-testid="trial-auto-addons-badge">
                    Auto-calculated from trial usage
                  </span>
                )}
              </div>
              {sub.status === 'trialing' && (
                <p className="text-[11px] text-gray-500 mb-2.5" data-testid="trial-auto-addons-hint">
                  We pre-filled add-ons to match what you're using right now. You can adjust them, but can't go below your current usage.
                </p>
              )}
              {isProvider && (
                <AddonCounter
                  label="Extra Organizations" value={extraOrgs} onChange={setExtraOrgs} price="30"
                  min={Math.max(0, (usage?.organizations?.current || 0) - 3)}
                />
              )}
              <AddonCounter
                label="Extra Companies" value={extraCompanies} onChange={setExtraCompanies} price={isProvider ? '9' : '29'}
                min={Math.max(0, (usage?.companies?.current || 0) - (isProvider ? 6 : 1))}
              />
              <AddonCounter
                label="Extra Admins" value={extraAdmins} onChange={setExtraAdmins} price={isProvider ? '7' : '11'}
                min={Math.max(0, (usage?.admins?.current || 0) - 1)}
              />
              <AddonCounter
                label="Extra Users" value={extraUsers} onChange={setExtraUsers} price={isProvider ? '5' : '9'}
                min={Math.max(0, (usage?.users?.current || 0) - 2)}
              />
            </div>
            <PriceEstimate
              estimate={estimate} isProvider={isProvider}
              extraOrgs={extraOrgs} extraCompanies={extraCompanies} extraAdmins={extraAdmins} extraUsers={extraUsers}
            />
            <Button
              onClick={handleCheckout}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white h-11 text-base font-semibold"
              disabled={checkoutLoading}
              data-testid="checkout-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> Subscribe — ${estimate?.total_monthly?.toFixed(2) || basePrice.replace('$', '')}/mo (billed monthly)</>
              )}
            </Button>
          </CardContent>
        </>
      )}
    </Card>
  );
}

function BasePlanRow({ planLabel, basePrice, baseDesc }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100">
      <div>
        <p className="text-sm font-medium text-gray-800">{planLabel} Base</p>
        <p className="text-xs text-gray-500">{baseDesc}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900">{basePrice}/mo</span>
    </div>
  );
}
